/**
 * Sistema de Control de Versiones y Actualizaciones Forzadas de la App
 * Permite gestionar versiones semver, purga de cachés PWA y validación de versiones mínimas.
 */

import { AppKillSwitchSettings } from '../types';

export const DEFAULT_APP_VERSION = '2.2.0';
export const STORAGE_KEY_APP_VERSION = 'nuestros_recuerdos_app_version';
export const STORAGE_KEY_UPDATED_AT = 'nuestros_recuerdos_app_version_updated_at';
export const STORAGE_KEY_JUST_UPDATED = 'nuestros_recuerdos_just_updated_flag';

/**
 * Compara dos versiones semver (ej: "2.2.0" vs "2.1.0" o "v2.2.0")
 * Retorna:
 *   1 si v1 > v2
 *   -1 si v1 < v2
 *   0 si v1 === v2
 */
export function compareSemver(v1: string, v2: string): number {
  const cleanV1 = (v1 || '0.0.0').trim().replace(/^[vV]/, '');
  const cleanV2 = (v2 || '0.0.0').trim().replace(/^[vV]/, '');

  const parts1 = cleanV1.split('.').map((p) => parseInt(p, 10) || 0);
  const parts2 = cleanV2.split('.').map((p) => parseInt(p, 10) || 0);

  const maxLen = Math.max(parts1.length, parts2.length, 3);
  for (let i = 0; i < maxLen; i++) {
    const p1 = parts1[i] !== undefined ? parts1[i] : 0;
    const p2 = parts2[i] !== undefined ? parts2[i] : 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
}

/**
 * Calcula de forma 100% automática la siguiente versión del sistema (ej: 2.2.0 -> 2.2.1)
 * El administrador nunca tiene que escribir ni gestionar números de versión manualmente.
 */
export function computeNextSystemVersion(currentVersion?: string): string {
  const base = (currentVersion || DEFAULT_APP_VERSION).trim().replace(/^[vV]/, '');
  const parts = base.split('.');
  if (parts.length >= 3) {
    const major = parseInt(parts[0], 10) || 2;
    const minor = parseInt(parts[1], 10) || 2;
    const patch = (parseInt(parts[2], 10) || 0) + 1;
    return `${major}.${minor}.${patch}`;
  }
  if (parts.length === 2) {
    return `${parts[0]}.${parts[1]}.1`;
  }
  return `${base}.1`;
}

/**
 * Obtiene la versión actualmente instalada/registrada en el cliente
 */
export function getClientAppVersion(): string {
  if (typeof window === 'undefined') return DEFAULT_APP_VERSION;
  try {
    const stored = localStorage.getItem(STORAGE_KEY_APP_VERSION);
    if (stored && stored.trim()) {
      return stored.trim().replace(/^[vV]/, '');
    }
  } catch {}
  return '1.0.0';
}

/**
 * Registra y guarda la versión actualizada en el almacenamiento local del cliente
 */
export function setClientAppVersion(version: string): void {
  if (typeof window === 'undefined') return;
  try {
    const clean = (version || DEFAULT_APP_VERSION).trim().replace(/^[vV]/, '');
    localStorage.setItem(STORAGE_KEY_APP_VERSION, clean);
    localStorage.setItem(STORAGE_KEY_UPDATED_AT, new Date().toISOString());

    // Notificar a otras pestañas/ventanas abiertas
    try {
      const bc = new BroadcastChannel('pareja_security_channel');
      bc.postMessage({
        type: 'APP_VERSION_UPDATED',
        version: clean,
      });
      bc.close();
    } catch {}
  } catch {}
}

/**
 * Detecta con máxima precisión si la aplicación se está ejecutando como una App Móvil / PWA instalada
 * (pantalla completa, standalone, iniciada desde el acceso directo de la pantalla de inicio o TWA)
 * o si se está ejecutando dentro de un navegador Web convencional (Chrome, Safari, Firefox, Edge, etc.).
 */
export function isInstalledAppRunning(): boolean {
  if (typeof window === 'undefined') return false;

  // 1. Si está dentro de un iframe (como el preview de desarrollo de AI Studio o embebido), es web
  if (window.self !== window.top) return false;

  // 2. Parámetros de URL que señalan lanzamiento desde icono de la PWA instalada (definido en manifest.json start_url)
  try {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get('source') === 'pwa' ||
      params.get('mode') === 'standalone' ||
      params.get('mode') === 'app' ||
      params.get('app') === '1' ||
      params.get('platform') === 'app'
    ) {
      sessionStorage.setItem('nuestros_recuerdos_is_installed_app', 'true');
      return true;
    }
  } catch {}

  // 3. Si en esta sesión de pestaña/app ya se determinó previamente que fue lanzada como app
  try {
    if (sessionStorage.getItem('nuestros_recuerdos_is_installed_app') === 'true') {
      return true;
    }
  } catch {}

  // 4. Modos de visualización de PWA instalada en Android / iOS / Windows / macOS
  if (typeof window.matchMedia === 'function') {
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches
    ) {
      return true;
    }
  }

  // 5. iOS Safari en pantalla de inicio (Standalone Web App)
  if ((window.navigator as any).standalone === true) {
    return true;
  }

  // 6. TWA / Aplicación nativa Android instalada (referrer)
  if (typeof document !== 'undefined' && document.referrer && document.referrer.startsWith('android-app://')) {
    return true;
  }

  // Si no cumple ninguno de los anteriores, el usuario está navegando en la WEB convencional
  return false;
}

/**
 * Verifica si la aplicación requiere actualización obligatoria o está inhabilitada.
 * REGLA CRÍTICA: Las actualizaciones son EXCLUSIVAMENTE para la App móvil instalada,
 * NUNCA para la web convencional.
 */
export function checkAppUpdateStatus(
  killSwitch?: AppKillSwitchSettings,
  options?: { isApp?: boolean }
): {
  isBlocked: boolean;
  isMaintenance: boolean;
  installedVersion: string;
  requiredVersion: string;
  isApp: boolean;
} {
  const isApp = options?.isApp !== undefined ? options.isApp : isInstalledAppRunning();
  const installedVersion = getClientAppVersion();
  const requiredVersion = (killSwitch?.minVersion || DEFAULT_APP_VERSION).trim().replace(/^[vV]/, '');

  // 1. Si no hay killSwitch o no está habilitado, la app está 100% operativa
  if (!killSwitch || !killSwitch.isEnabled) {
    return {
      isBlocked: false,
      isMaintenance: false,
      installedVersion,
      requiredVersion,
      isApp,
    };
  }

  const targetScope = killSwitch.targetScope || 'app_only';

  // 2. REGLA CLAVE: Si el alcance es "Solo App" (por defecto) y el usuario está navegando en la WEB, NUNCA bloquear
  if (targetScope === 'app_only' && !isApp) {
    return {
      isBlocked: false,
      isMaintenance: false,
      installedVersion,
      requiredVersion,
      isApp,
    };
  }

  // 3. Modo Mantenimiento / Bloqueo Total (nadie excepto admin ingresa)
  if (killSwitch.mode === 'maintenance') {
    return {
      isBlocked: true,
      isMaintenance: true,
      installedVersion,
      requiredVersion,
      isApp,
    };
  }

  // 4. Modo Forzar Actualización (default): Bloquea si la versión instalada es inferior a la requerida
  const isOutdated = compareSemver(installedVersion, requiredVersion) < 0;

  return {
    isBlocked: isOutdated,
    isMaintenance: false,
    installedVersion,
    requiredVersion,
    isApp,
  };
}

/**
 * Ejecuta el proceso de purga y actualización completa de la aplicación en el navegador/PWA:
 * 1. Elimina todos los caches de Service Worker
 * 2. Envía mensajes al Service Worker para skipWaiting y forzar activación
 * 3. Actualiza registros de Service Worker
 * 4. Almacena la nueva versión en localStorage
 * 5. Señaliza al sistema para no bloquear en el siguiente ciclo
 */
export async function performAppUpdateCycle(
  targetVersion: string,
  onStepProgress?: (stepText: string) => void
): Promise<void> {
  const cleanTarget = (targetVersion || DEFAULT_APP_VERSION).trim().replace(/^[vV]/, '');

  onStepProgress?.('Purgando caché del navegador y versiones anteriores...');

  // 1. Limpieza rigurosa de CacheStorage
  if (typeof window !== 'undefined' && 'caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch (err) {
      console.warn('[Update] Error al limpiar caches:', err);
    }
  }

  onStepProgress?.('Notificando al Service Worker de la PWA...');

  // 2. Enviar señal de SKIP_WAITING y CLEAR_CACHE a los Service Workers
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        if (reg.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          reg.waiting.postMessage({ type: 'PURGE_AND_UPDATE' });
        }
        if (reg.active) {
          reg.active.postMessage({ type: 'SKIP_WAITING' });
          reg.active.postMessage({ type: 'PURGE_AND_UPDATE' });
        }
        await reg.update().catch(() => {});
      }
    } catch (err) {
      console.warn('[Update] Error al actualizar SW:', err);
    }
  }

  onStepProgress?.('Guardando nueva versión verificada...');

  // 3. Almacenar la nueva versión en localStorage y registrar el flag de celebración
  setClientAppVersion(cleanTarget);
  try {
    sessionStorage.setItem(STORAGE_KEY_JUST_UPDATED, cleanTarget);
  } catch {}

  onStepProgress?.('¡Listo! Iniciando la versión actualizada...');
  await new Promise((resolve) => setTimeout(resolve, 400));
}
